import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useUserData } from '../../../hooks/useUserData';
import { userProfileService } from '../../../services/userProfileService';
import { useNavigate } from 'react-router-dom';
import { performLogout } from '../../../utils/logout';

type FormState = {
  name: string;
  cpf: string;
  phoneNumber: string;
};

type FormErrors = Partial<Record<keyof FormState, string>>;

const ProfilePage: React.FC = () => {
  const { user } = useUserData();
  const auth = useAuth();
  const navigate = useNavigate();
  const [formState, setFormState] = useState<FormState>({
    name: '',
    cpf: '',
    phoneNumber: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [savedValues, setSavedValues] = useState<Partial<FormState>>({});

  useEffect(() => {
    setFormState({
      name: user?.name || '',
      cpf: user?.cpf || '',
      phoneNumber: user?.phoneNumber || '',
    });
    setSavedValues({});
  }, [user?.name, user?.cpf, user?.phoneNumber]);

  const displayedData = useMemo(
    () => ({
      name: savedValues.name ?? user?.name ?? '-',
      email: user?.email ?? '-',
      phoneNumber: savedValues.phoneNumber ?? user?.phoneNumber ?? '-',
      cpf: savedValues.cpf ?? user?.cpf ?? '-',
      endereco: user?.endereco ?? '-',
    }),
    [savedValues, user]
  );

  const handleLogout = async () => {
    try {
      await performLogout(auth, navigate);
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  const isCpfValid = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length !== 11 || /^(\d)\1+$/.test(digits)) {
      return false;
    }

    const calculateDigit = (factor: number) => {
      let total = 0;
      for (let i = 0; i < factor - 1; i += 1) {
        total += Number(digits[i]) * (factor - i);
      }
      const remainder = (total * 10) % 11;
      return remainder === 10 ? 0 : remainder;
    };

    const firstDigit = calculateDigit(10);
    const secondDigit = calculateDigit(11);
    return firstDigit === Number(digits[9]) && secondDigit === Number(digits[10]);
  };

  const validateForm = () => {
    const errors: FormErrors = {};
    const normalizedName = formState.name.trim();
    const normalizedCpf = formState.cpf.trim();
    const normalizedPhone = formState.phoneNumber.trim();

    if (normalizedName && (normalizedName.length < 2 || normalizedName.length > 120)) {
      errors.name = 'Nome deve ter entre 2 e 120 caracteres.';
    }

    if (normalizedCpf) {
      if (!/^(\d{11}|\d{3}\.\d{3}\.\d{3}-\d{2})$/.test(normalizedCpf)) {
        errors.cpf = 'CPF deve estar no formato 000.000.000-00 ou conter 11 dígitos.';
      } else if (!isCpfValid(normalizedCpf)) {
        errors.cpf = 'CPF inválido.';
      }
    }

    if (normalizedPhone && !/^\+[1-9]\d{7,14}$/.test(normalizedPhone)) {
      errors.phoneNumber = 'Telefone deve estar no formato E.164, ex: +5511999999999.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFeedback(null);

    if (!validateForm()) {
      return;
    }

    const payload = {
      name: formState.name.trim() || undefined,
      cpf: formState.cpf.trim() || undefined,
      phoneNumber: formState.phoneNumber.trim() || undefined,
    };

    if (!payload.name && !payload.cpf && !payload.phoneNumber) {
      setFeedback({
        type: 'error',
        message: 'Preencha pelo menos um campo para atualizar.',
      });
      return;
    }

    setIsSaving(true);
    try {
      await userProfileService.atualizarDadosBasicos(payload);
      setSavedValues(payload);
      setIsEditing(false);
      setFeedback({
        type: 'success',
        message: 'Dados básicos atualizados com sucesso.',
      });
    } catch (error: any) {
      setFeedback({
        type: 'error',
        message: error?.response?.data?.message || 'Não foi possível atualizar os dados básicos.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const profileItems = [
    { label: 'Nome', value: displayedData.name },
    { label: 'E-mail', value: displayedData.email },
    { label: 'Telefone', value: displayedData.phoneNumber },
    { label: 'CPF', value: displayedData.cpf },
    { label: 'Endereço', value: displayedData.endereco },
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 md:px-6 md:py-12">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Perfil</h1>
        <p className="text-gray-600">Visualize seus dados cadastrais e gerencie sua sessão.</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-8">
        <div className="p-6 md:p-8 border-b border-gray-100 flex items-center justify-between gap-4">
          <h2 className="text-xl font-semibold text-gray-900">Dados do usuário</h2>
          <button
            type="button"
            onClick={() => {
              if (!isEditing) {
                setFormState({
                  name: displayedData.name === '-' ? '' : displayedData.name,
                  cpf: displayedData.cpf === '-' ? '' : displayedData.cpf,
                  phoneNumber: displayedData.phoneNumber === '-' ? '' : displayedData.phoneNumber,
                });
              }
              setFormErrors({});
              setFeedback(null);
              setIsEditing((prev) => !prev);
            }}
            className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
            title={isEditing ? 'Fechar edição' : 'Editar dados'}
            aria-label={isEditing ? 'Fechar edição' : 'Editar dados'}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />
            </svg>
          </button>
        </div>

        {!isEditing ? (
          <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {profileItems.map((item) => (
              <div key={item.label} className="flex flex-col gap-1">
                <span className="text-sm text-gray-500">{item.label}</span>
                <span className="text-base font-medium text-gray-900 break-words">{item.value}</span>
              </div>
            ))}
          </div>
        ) : (
          <form className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={handleSubmit}>
            <div className="flex flex-col gap-1">
              <label htmlFor="name" className="text-sm text-gray-600">Nome</label>
              <input
                id="name"
                type="text"
                value={formState.name}
                onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Seu nome completo"
              />
              {formErrors.name && <span className="text-sm text-red-600">{formErrors.name}</span>}
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="cpf" className="text-sm text-gray-600">CPF</label>
              <input
                id="cpf"
                type="text"
                value={formState.cpf}
                onChange={(event) => setFormState((prev) => ({ ...prev, cpf: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="000.000.000-00"
              />
              {formErrors.cpf && <span className="text-sm text-red-600">{formErrors.cpf}</span>}
            </div>

            <div className="flex flex-col gap-1 md:col-span-2">
              <label htmlFor="phoneNumber" className="text-sm text-gray-600">Telefone (E.164)</label>
              <input
                id="phoneNumber"
                type="text"
                value={formState.phoneNumber}
                onChange={(event) => setFormState((prev) => ({ ...prev, phoneNumber: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="+5511999999999"
              />
              {formErrors.phoneNumber && <span className="text-sm text-red-600">{formErrors.phoneNumber}</span>}
            </div>

            {feedback && (
              <div className={`md:col-span-2 text-sm ${feedback.type === 'success' ? 'text-green-700' : 'text-red-600'}`}>
                {feedback.message}
              </div>
            )}

            <div className="md:col-span-2 flex items-center gap-3">
              <button
                type="submit"
                disabled={isSaving}
                className="inline-flex items-center justify-center px-5 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Salvando...' : 'Salvar dados básicos'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setFormErrors({});
                  setFeedback(null);
                }}
                className="inline-flex items-center justify-center px-5 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Sessão</h3>
        <p className="text-gray-600 mb-6">Finalize sua sessão com segurança ao sair da plataforma.</p>
        <button
          onClick={handleLogout}
          className="inline-flex items-center justify-center px-5 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
        >
          Sair e fazer logout
        </button>
      </div>
    </div>
  );
};

export default ProfilePage;

